import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useLocation, useHistory } from 'react-router';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm, formValueSelector } from 'redux-form';

import type { RootState } from 'af-reducers';

import { TOOL_CREATE } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';

import * as ToolActions from 'af-actions/tool';

import Form from '../Shared/Form';
import FormModel from '../Shared/formModel';
import { validate } from '../Shared/validation';

type Props = CustomRouteComponentProps & ConnectedProps<typeof connector> & InjectedFormProps<FormModel>;

const Create = (props: Props) => {
	const { create, companyName, status, toolState } = props;

	const history = useHistory();
	const location = useLocation<{ orgAlias: string; }>();

	const { orgAlias } = location.state;

	const createItem = React.useCallback(async (form: FormModel) => {
		await create(FormModel.toRequestModel(form));
		history.push(CLIENT.COMPANY.EQUIPMENT.TOOL.LIST(orgAlias, companyName));
	}, [companyName, create, history, orgAlias]);

	return (
		<div className="form-segment">
			<Breadcrumbs
				items={[
					{ label: 'Equipment', url: CLIENT.COMPANY.EQUIPMENT.TOOL.LIST(orgAlias, companyName) },
					{ label: 'Small Tool' },
				]}
			/>
			<Form
				{...props}
				onSubmit={createItem}
				state={toolState}
				status={status}
			/>
		</div>
	);
};

const formSelector = formValueSelector(TOOL_CREATE);

interface FormActionWrapper {
	selector: <T extends keyof FormModel>(fieldName: T) => FormModel[T];
}
const formActionWrapper: Partial<FormActionWrapper> = {
	selector: undefined,
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	formActionWrapper.selector = (fieldName: keyof FormModel) => formSelector(state, fieldName);

	const status = formSelector(state, 'status');
	const toolState = formSelector(state, 'state');

	return {
		formActionWrapper: formActionWrapper as FormActionWrapper, // selector is no longer undefined
		companyName: companyData.name,
		status,
		toolState: toolState,
	};
}

function mapDispatchToProps() {
	return {
		create: ToolActions.create,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass>(
	connector,
	reduxForm({
		form: TOOL_CREATE,
		validate,
		enableReinitialize: true,
	})
);

export default enhance(Create);
